import {
    InvestorsDashboardData,
    MetricValue,
} from "@/features/investors/types/investors.types";
import { investorsFormatters as fmt } from "@/features/investors/utils/investors-formatters";

export type InsightItem = {
    title: string;
    detail: string;
};

export type SummaryChip = {
    icon: string;
    label: string;
};

export type QuarterPriority = {
    index: string;
    title: string;
    detail: string;
};

export type InvestorsInsights = {
    summary: {
        headline: string;
        body: string;
        chips: SummaryChip[];
    };
    notes: {
        growth: string;
        monetization: string;
        retention: string;
        corridors: string;
        operations: string;
        risk: string;
        network: string;
    };
    wins: InsightItem[];
    risks: InsightItem[];
    opportunities: InsightItem[];
    priorities: QuarterPriority[];
};

const value = (metric: MetricValue): number | null => metric.value;

const isUp = (metric: MetricValue): boolean =>
    metric.deltaPct !== null && metric.deltaPct > 0;

const revenueGrewEveryMonth = (data: InvestorsDashboardData): boolean => {
    const months = data.growth.monthly;
    if (months.length < 2) return false;
    return months.every((month, index) => index === 0 || month.revenue > months[index - 1].revenue);
};

const revenueEndToEndDeltaPct = (data: InvestorsDashboardData): number | null => {
    const months = data.growth.monthly;
    if (months.length < 2) return null;
    const first = months[0].revenue;
    const last = months[months.length - 1].revenue;
    if (first === 0) return null;
    return ((last - first) / first) * 100;
};

const topCorridorGmvSharePct = (data: InvestorsDashboardData): number | null => {
    const gmv = value(data.executive.gmv);
    const top = data.corridors.top[0];
    if (gmv === null || gmv === 0 || top === undefined) return null;
    return (top.gmv / gmv) * 100;
};

const HOUR_MS = 60 * 60 * 1000;

const buildSummary = (data: InvestorsDashboardData): InvestorsInsights["summary"] => {
    const revenue = data.executive.revenue;
    const margin = value(data.executive.grossMarginPct);
    const automation = value(data.operations.automationRatePct);
    const repeat = value(data.executive.repeatUsageRatePct);

    const accelerating = revenue.deltaPct !== null && revenue.deltaPct > 5;
    const marginUp = isUp(data.executive.grossMarginPct);
    const headline = accelerating
        ? marginUp
            ? "Growing with expanding margins."
            : "Growing, with margin under watch."
        : revenue.deltaPct !== null && revenue.deltaPct < -5
          ? "A contraction quarter."
          : "A stable quarter.";

    const sentences: string[] = [];
    if (revenue.value !== null) {
        const delta = revenue.deltaPct === null ? "" : ` (<span class="pos">${fmt.deltaPct(revenue.deltaPct)} QoQ</span>)`;
        sentences.push(
            `${data.period.label} closed with <span class="hl">${fmt.moneyCompact(revenue.value)} revenue</span>${delta}.`
        );
    }
    if (margin !== null) {
        sentences.push(`Gross margin stands at <span class="hl">${fmt.pct(margin)}</span>.`);
    }
    if (automation !== null) {
        sentences.push(
            `${fmt.pct(automation)} of completed operations ran without manual intervention.`
        );
    }
    if (repeat !== null) {
        sentences.push(
            `Repeat usage reached <span class="hl">${fmt.pct(repeat)}</span> of active accounts.`
        );
    }

    const chips: SummaryChip[] = [];
    if (revenue.deltaPct !== null) {
        chips.push({
            icon: revenue.deltaPct >= 0 ? "trending_up" : "trending_down",
            label: `Revenue ${fmt.deltaPct(revenue.deltaPct)}`,
        });
    }
    if (automation !== null) {
        chips.push({ icon: "bolt", label: `${fmt.pct(automation, 0)} automated` });
    }
    if (repeat !== null) {
        chips.push({ icon: "loop", label: `Repeat ${fmt.pct(repeat, 0)}` });
    }

    return { headline, body: sentences.join(" "), chips };
};

const buildNotes = (data: InvestorsDashboardData): InvestorsInsights["notes"] => {
    const months = data.growth.monthly;
    const lastMonth = months[months.length - 1];
    const endToEnd = revenueEndToEndDeltaPct(data);
    const growth =
        lastMonth !== undefined
            ? `<b>${lastMonth.label} was the strongest revenue month of the quarter</b> at ${fmt.moneyCompact(lastMonth.revenue)}${endToEnd !== null ? `, with revenue ${endToEnd >= 0 ? "up" : "down"} ${fmt.pct(Math.abs(endToEnd), 1)} from ${months[0].label} to ${lastMonth.label}` : ""}.`
            : "No completed operations in this period.";

    const feeRealization = value(data.monetization.feeRealizationPct);
    const composition = data.monetization.revenueComposition;
    const topLine = [...composition].sort((a, b) => b.value - a.value)[0];
    const compositionTotal = composition.reduce((total, item) => total + item.value, 0);
    const monetization = `${feeRealization !== null ? `Fee realization sits at <b>${fmt.pct(feeRealization)}</b> of expected fees. ` : ""}${topLine !== undefined && compositionTotal > 0 ? `<b>${topLine.label}</b> remain the largest revenue line at ${fmt.pct((topLine.value / compositionTotal) * 100, 0)} of the mix.` : ""}`;

    const repeat = data.retention.repeatUsageRatePct;
    const cohorts = data.retention.cohortRetention.filter(
        (row) => row.size > 0 && row.retentionPct.some((cell) => cell !== null && cell < 100)
    );
    const retention = `${repeat !== null ? `<b>${fmt.pct(repeat)} of active accounts placed 2+ operations.</b> ` : ""}${data.retention.newAccounts > 0 ? `${fmt.num(data.retention.newAccounts)} accounts transacted for the first time this quarter${cohorts.length > 0 ? ` and each monthly cohort can be tracked below` : ""}.` : ""}`;

    const topCorridor = data.corridors.top[0];
    const share = topCorridorGmvSharePct(data);
    const corridors =
        topCorridor !== undefined
            ? `<b>${topCorridor.corridor} is the leading corridor</b> with ${fmt.moneyCompact(topCorridor.gmv)} GMV${share !== null ? ` (${fmt.pct(share, 0)} of the reporting-currency total)` : ""}${topCorridor.marginPct !== null ? ` at a ${fmt.pct(topCorridor.marginPct, 0)} profit margin on revenue` : ""}.`
            : "No corridor activity in this period.";

    const p50 = data.operations.duration.p50Ms;
    const automation = value(data.operations.automationRatePct);
    const operations = `${p50 !== null ? `Median completion time held at <b>${fmt.durationMs(p50)}</b>` : "Completion time is unavailable"}${automation !== null ? ` with <b>${fmt.pct(automation)}</b> of operations fully automated` : ""}.`;

    const review = value(data.risk.reviewRatePct);
    const atRisk = value(data.risk.revenueAtRisk);
    const risk = `Review rate is <b>${review !== null ? fmt.pct(review) : "—"}</b>${atRisk !== null ? ` and ${fmt.money(atRisk)} of revenue sits in reviewed, blocked or in-flight operations` : ""}.`;

    const used = value(data.contacts.contactsUsedInPeriod);
    const network =
        used !== null
            ? `${fmt.numShort(used)} contacts were referenced by operations this period (proxy join on contact email).`
            : "No contact activity recorded in this period.";

    return { growth, monetization, retention, corridors, operations, risk, network };
};

const buildWins = (data: InvestorsDashboardData): InsightItem[] => {
    const wins: InsightItem[] = [];
    const endToEnd = revenueEndToEndDeltaPct(data);

    if (revenueGrewEveryMonth(data) && endToEnd !== null) {
        wins.push({
            title: "Revenue accelerated through the quarter",
            detail: `Revenue grew every month, ending ${fmt.pct(endToEnd, 1)} above the opening month.`,
        });
    }

    const automation = value(data.operations.automationRatePct);
    const manual = value(data.operations.manualRatePct);
    if (automation !== null && manual !== null && manual < 10) {
        wins.push({
            title: `${fmt.pct(automation, 1)} automation — scaling without headcount`,
            detail: `Only ${fmt.pct(manual, 1)} of completed operations needed manual intervention.`,
        });
    }

    const repeat = value(data.executive.repeatUsageRatePct);
    if (repeat !== null && repeat >= 50) {
        wins.push({
            title: `Repeat usage at ${fmt.pct(repeat, 0)} of active accounts`,
            detail: `${fmt.num(data.retention.accounts2Plus)} accounts placed 2+ operations and ${fmt.num(data.retention.accounts5Plus)} reached 5+.`,
        });
    }

    const margin = data.executive.grossMarginPct;
    if (margin.value !== null && margin.previous !== null && margin.value > margin.previous) {
        wins.push({
            title: `Gross margin expanded to ${fmt.pct(margin.value)}`,
            detail: `Up from ${fmt.pct(margin.previous)} in ${data.period.previousLabel}.`,
        });
    }

    return wins.slice(0, 3);
};

const buildRisks = (data: InvestorsDashboardData): InsightItem[] => {
    const risks: InsightItem[] = [];

    const margin = data.executive.grossMarginPct;
    if (margin.value !== null && margin.previous !== null && margin.value < margin.previous) {
        risks.push({
            title: "Gross margin compressed quarter-over-quarter",
            detail: `Margin moved from ${fmt.pct(margin.previous)} to ${fmt.pct(margin.value)} — pricing and provider costs need review.`,
        });
    }

    const share = topCorridorGmvSharePct(data);
    const top = data.corridors.top[0];
    if (share !== null && top !== undefined && share > 40) {
        risks.push({
            title: `GMV concentration in ${top.corridor}`,
            detail: `The top corridor carries ${fmt.pct(share, 0)} of reporting-currency GMV — diversification reduces single-corridor exposure.`,
        });
    }

    const p90 = data.operations.duration.p90Ms;
    if (p90 !== null && p90 > HOUR_MS) {
        const slowestLeg = [...data.operations.legs]
            .filter((leg) => leg.p90Ms !== null)
            .sort((a, b) => (b.p90Ms ?? 0) - (a.p90Ms ?? 0))[0];
        risks.push({
            title: `Slow tail: p90 completion at ${fmt.durationMs(p90)}`,
            detail: slowestLeg !== undefined && slowestLeg.p90Ms !== null
                ? `The ${slowestLeg.leg} leg dominates the tail at ${fmt.durationMs(slowestLeg.p90Ms)} p90.`
                : "The slowest decile of operations exceeds one hour.",
        });
    }

    const negative = value(data.monetization.negativeMarginRatePct);
    if (negative !== null && negative > 5) {
        risks.push({
            title: `${fmt.pct(negative, 1)} of operations complete with negative margin`,
            detail: "Loss-making operations above the 5% tolerance — re-price or block the offending segments.",
        });
    }

    if (data.retention.newAccounts > 0 && data.retention.reactivatedAccounts < data.retention.newAccounts * 0.1) {
        risks.push({
            title: "Reactivation trails new-account growth",
            detail: `${fmt.num(data.retention.reactivatedAccounts)} reactivated vs ${fmt.num(data.retention.newAccounts)} new — the dormant base is under-worked.`,
        });
    }

    return risks.slice(0, 3);
};

const buildOpportunities = (data: InvestorsDashboardData): InsightItem[] => {
    const opportunities: InsightItem[] = [];

    const margins = data.corridors.top
        .map((corridor) => corridor.marginPct)
        .filter((margin): margin is number => margin !== null)
        .sort((a, b) => a - b);
    const medianMargin = margins.length > 0 ? margins[Math.floor(margins.length / 2)] : null;
    const repricing = data.corridors.top.find(
        (corridor) =>
            corridor.marginPct !== null &&
            medianMargin !== null &&
            corridor.marginPct < medianMargin &&
            corridor.operations >= 50
    );
    if (repricing !== undefined && repricing.marginPct !== null) {
        opportunities.push({
            title: `Re-price ${repricing.corridor}`,
            detail: `${fmt.moneyCompact(repricing.gmv)} GMV at a ${fmt.pct(repricing.marginPct, 0)} margin — below the corridor median.`,
        });
    }

    const manual = value(data.operations.manualRatePct);
    if (manual !== null && manual > 1) {
        opportunities.push({
            title: "Push automation further",
            detail: `Removing the remaining ${fmt.pct(manual, 1)} manual share lifts operational leverage without headcount.`,
        });
    }

    const accounts5Plus = data.retention.accounts5Plus;
    if (opportunities.length < 3 && accounts5Plus > 0) {
        opportunities.push({
            title: "Deepen the power-account base",
            detail: `${fmt.num(accounts5Plus)} accounts already run 5+ operations a quarter — a ready segment for higher limits and new rails.`,
        });
    }

    return opportunities.slice(0, 3);
};

const buildPriorities = (data: InvestorsDashboardData): QuarterPriority[] => {
    const priorities: QuarterPriority[] = [];
    const manual = value(data.operations.manualRatePct);
    const automation = value(data.operations.automationRatePct);

    if (manual !== null && manual > 10) {
        priorities.push({
            index: "01",
            title: "Reduce manual operations",
            detail: `Manual rate is ${fmt.pct(manual, 1)} — automate the top intervention reasons.`,
        });
    } else if (automation !== null) {
        priorities.push({
            index: "01",
            title: `Hold automation above ${fmt.pct(Math.floor(automation), 0)}`,
            detail: "Keep the no-touch rate as volume scales.",
        });
    }

    const top = data.corridors.top[0];
    if (top !== undefined) {
        priorities.push({
            index: "02",
            title: `Scale the ${top.corridor} corridor`,
            detail: `${fmt.moneyCompact(top.gmv)} GMV today — deepen payout capacity and lift volume caps.`,
        });
    }

    priorities.push({
        index: "03",
        title: "Tighten review throughput",
        detail: "Keep completion-after-review high while volume grows.",
    });

    return priorities.slice(0, 4);
};

const build = (data: InvestorsDashboardData): InvestorsInsights => ({
    summary: buildSummary(data),
    notes: buildNotes(data),
    wins: buildWins(data),
    risks: buildRisks(data),
    opportunities: buildOpportunities(data),
    priorities: buildPriorities(data),
});

export const investorsInsights = { build };
